import { type ObjectDirective } from 'vue';

interface IntersectionObserverOptions extends IntersectionObserverInit {
  /** Disconnect the IntersectionObserver after the first intersection */
  once?: boolean;
  /** Function to determine if the element is intersecting the viewport */
  isIntersecting?: (entry: IntersectionObserverEntry) => boolean;
}

type BindingValueFunction = () => void;
type BindingValue = BindingValueFunction | [BindingValueFunction, IntersectionObserverOptions];

/**
 * Directive that calls the provided callback when the element intersects the viewport.
 *
 * ```html
 * <div v-intersected="callback">   <!-- calls the callback when the element intersects the viewport -->
 * <div v-intersected="[callback, options]">   <!-- calls the callback when the element intersects the viewport -->
 * ```
 *
 * options accept all the IntersectionObserverInit options and an additional `once` option and `isIntersecting` function.
 * The `once` option disconnects the observer after the first intersection.
 * The `isIntersecting` function is used to determine if the element is intersecting the viewport.
 *
 * ```html
 * <div v-intersected="[callback, { rootMargin: '10px', threshold: 0.5, once: true }]">
 * <div v-intersected="[callback, { isIntersecting: entry => entry.intersectionRatio > 0.5 }]">
 * ```
 */
export const vIntersected: ObjectDirective<HTMLElement, BindingValue> = {
  mounted(el, binding) {
    binding.dir.beforeUnmount =
      typeof binding.value === 'function' ? createIO(el, binding.value) : createIO(el, ...binding.value);
  },
};

const defaultIsIntersecting = (entry: IntersectionObserverEntry) => entry.isIntersecting;

function createIO(el: HTMLElement, callback: BindingValueFunction, options: IntersectionObserverOptions = {}) {
  const isIntersecting = options.isIntersecting ?? defaultIsIntersecting;

  const stop = () => io.disconnect();

  const io = new IntersectionObserver(([entry]) => {
    if (isIntersecting(entry)) {
      callback();
      if (options.once) stop();
    }
  }, options);

  io.observe(el);

  return stop;
}
